/**
 * Revision group header row for the Insights tab table.
 *
 * Renders a full-width section divider inside an antd Table body.
 * Shows: caret / revision label / Active badge / date range / per-revision subtotal summary.
 *
 * Returned as the `children` of a merged cell from the first dimension column's
 * `render` for a sentinel row. antd wraps this in the <td> and applies the colSpan
 * via the render's `props.colSpan` — so this component renders ONLY the cell's inner
 * content (no <tr>/<td> of its own).
 */

import React from 'react'
import { CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons'
import type { RevisionMeta, RevisionSubtotals, JourneyPerformanceReport } from './revision-insights'
import { revisionDisplayLabel } from './revision-insights'
import { RevisionActiveTag } from './revision-active-tag'
import { numberWithCommas } from '../../../_utils/utils'
import { formatRateOrBlank } from '../../reporting_v2/helpers/format-rate'

interface RevisionGroupHeaderProps {
    meta: RevisionMeta
    subtotals: RevisionSubtotals
    collapsed: boolean
    onToggle: () => void
    /**
     * How many table columns this header spans. Retained for callers/tests;
     * the actual cell colSpan is applied by antd via the column render's
     * `props.colSpan`, so this component no longer renders its own <td>.
     */
    colSpan: number
    /** Report type — controls which subtotal fields appear in the summary. */
    report: JourneyPerformanceReport
    /** Whether the table is showing purchase columns, so the summary matches it. */
    showPurchases?: boolean
}

export function buildSummaryText(
    subtotals: RevisionSubtotals,
    report: JourneyPerformanceReport,
    showPurchases: boolean = false,
): string {
    const fmt = numberWithCommas
    // Rates here read exactly as they do in the columns underneath, blank for a zero included.
    const pct = formatRateOrBlank

    // Appended from the same subtotals the purchase columns aggregate, so the header
    // and the column values can never disagree.
    const purchaseSummary = showPurchases
        ? ` · ${fmt(subtotals.purchases)} purchases · P. Rate ${pct(subtotals.purchaseRate)}`
        : ''

    if (report === 'STEPS') {
        return `${fmt(subtotals.entrances)} entrants${purchaseSummary}`
    }
    if (report === 'SPLIT_TEST_VARIANTS') {
        return `${fmt(subtotals.entrances)} entrants · ${fmt(subtotals.impressions)} impressions · CTR ${pct(subtotals.ctr)}${purchaseSummary}`
    }
    // NOTIFICATIONS
    return `${fmt(subtotals.impressions)} impressions · CTR ${pct(subtotals.ctr)}${purchaseSummary}`
}

export const RevisionGroupHeader: React.FC<RevisionGroupHeaderProps> = ({
    meta,
    subtotals,
    collapsed,
    onToggle,
    // colSpan is applied by antd on the wrapping <td>; not used directly here.
    colSpan: _colSpan,
    report,
    showPurchases = false,
}) => {
    return (
        <div
            className="revision-group-header-row"
            data-revision-id={meta.id}
            role="button"
            tabIndex={0}
            aria-expanded={!collapsed}
            aria-label={`${revisionDisplayLabel(meta)} revision group, ${collapsed ? 'collapsed' : 'expanded'}`}
            onClick={onToggle}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onToggle()
                }
            }}
        >
            <div className="rgh-content">
                <span className="rgh-caret" aria-hidden={true}>
                    {collapsed ? <CaretRightOutlined /> : <CaretDownOutlined />}
                </span>
                <span className="rgh-tag">{revisionDisplayLabel(meta)}</span>
                {meta.isActive && <RevisionActiveTag />}
                <span className="rgh-date">· {meta.dateRange}</span>
                <span className="rgh-summary">{buildSummaryText(subtotals, report, showPurchases)}</span>
            </div>
        </div>
    )
}
